'use client'

import { Children, cloneElement, isValidElement, useRef, type CSSProperties, type ReactElement, type ReactNode } from 'react'
import { useMovingRail } from '@/hooks/use-moving-rail'
import { MovingRailControls } from '@/components/brand-kit/moving-rail-controls'

/** Adapt the canvas logo/text bands to the same controls as product marquees. */
export function CanvasMovingBand({ children, label, className, style, showControls = true }: {
  showControls?: boolean
  children?: ReactNode
  label: string
  className?: string
  style?: CSSProperties
}) {
  const rail = useRef<HTMLDivElement>(null)
  const interaction = useRef<HTMLDivElement>(null)
  // Generated canvas children can include whitespace beside the track.
  const track = Children.toArray(children).find(isValidElement) as ReactElement<{ children: ReactNode; style?: CSSProperties }> | undefined
  const sets = Children.toArray(track?.props.children).filter(isValidElement) as ReactElement<Record<string, unknown>>[]
  const motion = useMovingRail(rail, sets.length, 24, interaction)
  if (!track) return null
  return (
    <div ref={interaction} style={{ display: 'contents' }}>
      {showControls ? <MovingRailControls label={label} {...motion} /> : null}
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- Named scroll regions require keyboard entry; verified by rendered axe and native scrolling. */}
      <div ref={rail} role="region" aria-label={label} tabIndex={0} className={className}
        style={{ ...style, overflowX: 'auto', cursor: 'grab', userSelect: 'none' }}>
        {cloneElement(track, { style: { ...track.props.style, animation: 'none', transform: 'none' } },
          [...sets, ...(sets.length ? [sets[0]] : [])].map((set, index) => cloneElement(set, {
            key: index,
            'data-rail-first': index === 0 || undefined,
            'data-rail-repeat': index === 1 || undefined,
            'aria-hidden': index > 0 || undefined,
          })),
        )}
      </div>
    </div>
  )
}
